import { DateTime } from 'luxon';

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';

import { NgIconComponent } from '@ng-icons/core';

import { ErrorSmallComponent } from '@components/error-handling/error-small/error-small.component';

import { ErrorStore, UserStore } from '@cxc/core/services';
import { UsersAPI } from '@services/API/identity/users.service';
import { FormService } from '@services/form.service';

import { CustomValidators } from '@cxc/core/utils';

const MIN_SIGNUP_AGE = 18;
const MAX_SIGNUP_AGE = 120;

@Component({
  selector: 'general-edit',
  templateUrl: './general-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    DatePickerModule,
    InputTextModule,
    ButtonModule,
    RippleModule,

    NgIconComponent,

    ErrorSmallComponent,
  ],
})
export class GeneralEditComponent implements OnInit, AfterViewInit {
  formService = inject(FormService);
  errorStore = inject(ErrorStore);

  close = output<void>();

  form!: FormGroup;

  // Bounds for the birthdate picker: no future dates, no under-18, no unreasonably old dates
  minBirthdate = DateTime.now().minus({ years: MAX_SIGNUP_AGE }).toJSDate();
  maxBirthdate = DateTime.now().minus({ years: MIN_SIGNUP_AGE }).toJSDate();

  constructor(
    private usersAPI: UsersAPI,
    private userStore: UserStore,
    fb: FormBuilder
  ) {
    this.form = fb.group({
      firstName: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.pattern(
            new RegExp(/^[a-zA-Z\xC0-\xFF ]*$/),
            'first name'
          ),
          CustomValidators.minLength(4, 'first name'),
          CustomValidators.maxLength(50, 'first name'),
        ],
      ],
      lastName: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.pattern(
            new RegExp(/^[a-zA-Z\xC0-\xFF ]*$/),
            'last name'
          ),
          CustomValidators.minLength(4, 'last name'),
          CustomValidators.maxLength(50, 'last name'),
        ],
      ],
      birthdate: [
        '',
        [
          CustomValidators.required(),
          CustomValidators.minAge(MIN_SIGNUP_AGE, MAX_SIGNUP_AGE),
        ],
      ],
    });
  }

  ngOnInit(): void {
    this.errorStore.clear();
  }

  ngAfterViewInit(): void {
    this.form.patchValue({
      firstName: this.userStore.get().first_name,
      lastName: this.userStore.get().last_name,
      birthdate: DateTime.fromFormat(
        this.userStore.get().birthdate!,
        'yyyy-MM-dd'
      ).toJSDate(),
    });
  }

  clickCancel() {
    this.close.emit();
  }

  submit() {
    if (this.formService.trySubmitForm(this.form)) {
      return;
    }

    this.usersAPI
      .patch({
        first_name: this.form.value['firstName'],
        last_name: this.form.value['lastName'],
        birthdate: DateTime.fromJSDate(this.form.value['birthdate']).toFormat(
          'yyyy-MM-dd'
        ),
      })
      .subscribe({
        next: response => {
          this.userStore.patch(response);
          this.close.emit();
        },
      });
  }
}
